import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { IconType } from 'react-icons';
import { IoIosArrowDown, IoIosArrowForward } from 'react-icons/io';
import routePaths from 'src/contstant/routePaths';
import { useBusinessUnit } from 'src/store/businessUnit/useBusinessUnit';
import useUserProfile from 'src/hooks/useUserProfile';
import { useUserPreferences } from 'src/store/userPreferences/useUserPreferences';
import { Authorized } from 'src/components/Authorized';
import permissionAccessConfig from 'src/contstant/permissionAccessConfig';

import './CustomSideNavigation.css';
import { TbClipboardList, TbSettings2 } from 'react-icons/tb';
import { GoHome } from 'react-icons/go';
import { GoQuestion } from 'react-icons/go';
import { GoTasklist } from 'react-icons/go';
import { GoCommentDiscussion } from 'react-icons/go';
import { GoDownload } from 'react-icons/go';
import { GoTools } from 'react-icons/go';
import { LiaUserShieldSolid } from 'react-icons/lia';
import { LiaObjectUngroupSolid } from 'react-icons/lia';
import { AiOutlineUsergroupAdd } from 'react-icons/ai';
import { MdOutlineAddLocationAlt } from 'react-icons/md';
import { BsTags } from 'react-icons/bs';
import { GrTemplate } from 'react-icons/gr';
import { RiFolderUnknowLine } from 'react-icons/ri';
import { IoSettingsOutline } from 'react-icons/io5';
import { IoCalendarOutline } from 'react-icons/io5';
import { TextContent } from '@cloudscape-design/components';
const CustomSideNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeHref, setActiveHref] = useState(location.pathname);

  useEffect(() => {
    setActiveHref(location.pathname);
  }, [location.pathname]);

  const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);
  const { getUserBusinessUnitName } = useUserProfile();

  const handleNavigation = (href: string) => {
    setActiveHref(href);
    navigate(href);
  };

  const NavItem = ({
    icon: Icon,
    text,
    href,
    isSubItem = false,
  }: {
    icon: any;
    text: string;
    href: string;
    isSubItem?: boolean;
  }) => (
    <div
      data-testid={`nav-item-${text.toLowerCase().replace(/\s+/g, '-')}`}
      onClick={() => handleNavigation(href)}
      className={`nav-item ${activeHref === href ? 'active' : ''} ${isSubItem ? 'sub-item' : ''}`}
    >
      <Icon size={20} />
      <TextContent>
        <p>{text}</p>
      </TextContent>
    </div>
  );

  const ExpandableNavItem = ({
    icon: Icon,
    text,
    href,
    isExpanded,
    onToggle,
  }: {
    icon: any;
    text: string;
    href: string;
    isExpanded: boolean;
    onToggle: () => void;
  }) => (
    <div
      data-testid={`expandable-nav-item-${text.toLowerCase().replace(/\s+/g, '-')}`}
      onClick={(e) => {
        e.stopPropagation();
        if (href) handleNavigation(href);
        onToggle();
      }}
      className={`expandable-nav-item ${activeHref === href ? 'active' : ''}`}
    >
      <Icon size={20} />
      <TextContent>
        <p>{text}</p>
      </TextContent>

      {isExpanded ? (
        <IoIosArrowDown
          size={14}
          className="expand-arrow"
          data-testid="expand-arrow-down"
        />
      ) : (
        <IoIosArrowForward
          size={14}
          className="expand-arrow"
          data-testid="expand-arrow-forward"
        />
      )}
    </div>
  );

  return (
    <div className="side-navigation" data-testid="side-navigation">
      <div className="side-navigation-header" data-testid="side-navigation-header">
        {getUserBusinessUnitName()}
      </div>
      <NavItem icon={GoHome} text="Home" href="/" />
      <NavItem
        icon={TbClipboardList}
        text={routePaths.AUDITS.title}
        href={routePaths.AUDITS.path}
      />
      <NavItem
        icon={GoQuestion}
        text={routePaths.QUESTION.title}
        href={routePaths.QUESTION.path}
      />
      <NavItem
        icon={GoTasklist}
        text={routePaths.TASKS.title}
        href={routePaths.TASKS.path}
      />
      <NavItem icon={GoDownload} text="Reports" href="/reports" />
      <ExpandableNavItem
        icon={IoSettingsOutline}
        text={routePaths.SETTINGS.title}
        href={routePaths.SETTINGS.path}
        isExpanded={isSettingsExpanded}
        onToggle={() => setIsSettingsExpanded(!isSettingsExpanded)}
      />
      {isSettingsExpanded && (
        <div data-testid="settings-submenu">
          <NavItem
            icon={LiaUserShieldSolid}
            text={routePaths.ROLES_PERMISSIONS.title}
            href={routePaths.ROLES_PERMISSIONS.path}
            isSubItem
          />
          <NavItem
            icon={LiaObjectUngroupSolid}
            text={routePaths.BUSINESS_UNITS_DIVISIONS.title}
            href={routePaths.BUSINESS_UNITS_DIVISIONS.path}
            isSubItem
          />
          <NavItem
            icon={AiOutlineUsergroupAdd}
            text={routePaths.USERS.title}
            href={routePaths.USERS.path}
            isSubItem
          />
          <NavItem
            icon={MdOutlineAddLocationAlt}
            text={routePaths.LOCATIONS.title}
            href={routePaths.LOCATIONS.path}
            isSubItem
          />
          <NavItem
            icon={BsTags}
            text={routePaths.TAGS.title}
            href={routePaths.TAGS.path}
            isSubItem
          />
          <NavItem
            icon={RiFolderUnknowLine}
            text={routePaths.CATEGORIES.title}
            href={routePaths.CATEGORIES.path}
            isSubItem
          />
          <NavItem
            icon={GrTemplate}
            text={routePaths.TEMPLATE.title}
            href={routePaths.TEMPLATE.path}
            isSubItem
          />
          <NavItem
            icon={IoCalendarOutline}
            text={routePaths.SCHEDULES.title}
            href={routePaths.SCHEDULES.path}
            isSubItem
          />
          <Authorized
            permissionsToCheck={[permissionAccessConfig.DIVISION.EDIT_DIVISION]}
          >
            <NavItem
              icon={TbSettings2}
              text={routePaths.CONFIGURATION.title}
              href={routePaths.CONFIGURATION.path}
              isSubItem
            />
          </Authorized>
          <Authorized
            permissionsToCheck={[permissionAccessConfig.FEEDBACK.EXPORT_FEEDBACK]}
          >
            <NavItem
              icon={GoCommentDiscussion}
              text={routePaths.FEEDBACK.title}
              href={routePaths.FEEDBACK.path}
              isSubItem
            />
          </Authorized>
        </div>
      )}
    </div>
  );
};

export default CustomSideNavigation;
